/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select, { GroupBase } from 'react-select';

import { DocumentManagerDocument } from 'services/ai_management_document_requirements';
import { formatDate } from 'services/ai_management_dates';
import { Container } from './styles';

interface Option {
  value: string;
  label: string;
  document: DocumentManagerDocument;
}

interface AiManagementDocumentManagerSelectProps {
  documents: DocumentManagerDocument[];
  loading: boolean;
  error: boolean;
  disabled?: boolean;
  busy?: boolean;
  label?: string;
  onOpen: () => void;
  onSelect: (documentId: string) => void;
}

const groupOptions = (documents: DocumentManagerDocument[]) => {
  const groups = new Map<string, Option[]>();
  documents.forEach((document) => {
    const options = groups.get(document.group) ?? [];
    options.push({ value: document.id, label: document.name, document });
    groups.set(document.group, options);
  });

  return Array.from(groups, ([label, options]) => ({ label, options }));
};

const renderOptionLabel = (document: DocumentManagerDocument) => (
  <div className="d-flex align-items-center justify-content-between">
    <span className="text-truncate mr-2">{document.name}</span>
    {document.expiresOn && (
      <small className={`flex-shrink-0 ${document.expired ? 'text-danger' : 'text-muted'}`}>
        {document.expired ? 'Pendente · vencido em ' : 'Vence em '}
        {formatDate(document.expiresOn)}
      </small>
    )}
  </div>
);

const AiManagementDocumentManagerSelect: React.FC<AiManagementDocumentManagerSelectProps> = ({
  documents,
  loading,
  error,
  disabled = false,
  busy = false,
  label = 'Importar do Gerenciar Documentos',
  onOpen,
  onSelect,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const options = useMemo(() => groupOptions(documents), [documents]);

  useEffect(() => {
    if (!open) return undefined;

    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const toggle = () => {
    if (!open) onOpen();
    setOpen((prev) => !prev);
  };

  const emptyMessage = () => {
    if (error) return 'Não foi possível carregar os documentos do Gerenciar Documentos.';
    return 'Nenhum documento disponível no Gerenciar Documentos.';
  };

  return (
    <Container ref={ref} className="position-relative">
      <button
        type="button"
        className="btn btn-outline-secondary d-inline-flex align-items-center"
        onClick={toggle}
        disabled={disabled || busy}
        aria-expanded={open}
      >
        {busy ? 'Importando...' : label}
        <i className="material-icons ml-1">arrow_drop_down</i>
      </button>

      {open && (
        <div className="document-manager-menu shadow">
          <Select<Option, false, GroupBase<Option>>
            autoFocus
            menuIsOpen
            options={options}
            isLoading={loading}
            placeholder="Buscar documento..."
            loadingMessage={() => 'Carregando documentos...'}
            noOptionsMessage={emptyMessage}
            formatOptionLabel={(option) => renderOptionLabel(option.document)}
            isOptionDisabled={(option) => option.document.expired}
            classNamePrefix="document-manager-select"
            onChange={(selected) => {
              if (!selected) return;
              setOpen(false);
              onSelect(selected.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setOpen(false);
            }}
          />
        </div>
      )}
    </Container>
  );
};

export default AiManagementDocumentManagerSelect;
